import { useState, type FormEvent } from 'react';
import { Link, NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useMeta } from '../meta';
import { DangerZone, ErrorBox, Panel, fmtDate } from '../components';
import { TeamTab } from './ManageTeam';
import { ApiTab } from './ManageApi';
import { ImportTab } from './ManageImport';
import { N_, t, tp } from '../i18n';
import { usePublicConfig } from './Auth';
import { UpdateTab } from './ManageUpdate';

export function ManagePage() {
  const { meta, th } = useMeta();
  const isAdmin = meta.user.access_level >= th('manage_user');
  const cfg = usePublicConfig().data;
  const mantis = cfg?.mantis_database === true;
  return (
    <div>
      <div className="page-head">
        <h1>{t('Manage')}</h1>
        <nav className="tabs">
          <NavLink to="/manage/team">{t('Team')}</NavLink>
          <NavLink to="/manage/projects">{t('Projects')}</NavLink>
          {isAdmin && <NavLink to="/manage/custom-fields">{t('Custom Fields')}</NavLink>}
          {isAdmin && !mantis && <NavLink to="/manage/import">{t('Import')}</NavLink>}
          {isAdmin && <NavLink to="/manage/api">{t('API')}</NavLink>}
          {meta.can_update_application && <NavLink to="/manage/update">{t('Application updates')}</NavLink>}
        </nav>
      </div>
      <Routes>
        <Route index element={isAdmin ? <TeamTab /> : <ProjectsTab />} />
        <Route path="team" element={<TeamTab />} />
        <Route path="users" element={<TeamTab />} />
        <Route path="projects" element={<ProjectsTab />} />
        <Route path="projects/:id" element={<ProjectEdit />} />
        <Route path="custom-fields" element={<CustomFieldsTab />} />
        <Route path="api" element={<ApiTab />} />
        {!mantis && <Route path="import" element={<ImportTab />} />}
        {meta.can_update_application && <Route path="update" element={<UpdateTab />} />}
      </Routes>
    </div>
  );
}

function useInvalidate(...keys: string[]) {
  const qc = useQueryClient();
  return () => {
    for (const k of keys) qc.invalidateQueries({ queryKey: [k] });
  };
}

// ---------------------------------------------------------------------------
function ProjectsTab() {
  const { meta, label, th } = useMeta();
  const { data, error } = useQuery({ queryKey: ['admin-projects'], queryFn: () => api.get<any[]>('/api/admin/projects') });
  const [name, setName] = useState('');
  const invalidate = useInvalidate('admin-projects', 'meta');
  const create = useMutation({
    mutationFn: () => api.post('/api/admin/projects', { name }),
    onSuccess: () => {
      setName('');
      invalidate();
    },
  });
  const byId = new Map((data ?? []).map((p) => [p.id, p]));
  const isAdmin = meta.user.access_level >= th('create_project');
  return (
    <Panel
      title={t('Projects')}
      actions={
        isAdmin && (
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('New project name')} />
            <button className="primary">{t('Create Project')}</button>
          </form>
        )
      }
    >
      <ErrorBox error={error || create.error} />
      {data && data.length === 0 && isAdmin && <div className="banner info">{t("Create your team's first project to start tracking issues.")}</div>}
      <table className="simple">
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Parent')}</th>
            <th>{t('Status')}</th>
            <th>{t('Enabled')}</th>
            <th>{t('View Status')}</th>
            <th className="num">{t('Issues')}</th>
            <th>{t('Description')}</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((p) => (
            <tr key={p.id} className={p.enabled ? '' : 'disabled'}>
              <td>
                <Link to={`/manage/projects/${p.id}`}>{p.name}</Link>
              </td>
              <td className="muted">{p.parent_ids.map((id: number) => byId.get(id)?.name ?? `#${id}`).join(', ')}</td>
              <td>{label('project_status', p.status)}</td>
              <td>{p.enabled ? t('yes') : t('no')}</td>
              <td>{label('view_state', p.view_state)}</td>
              <td className="num">{p.issue_count}</td>
              <td className="muted">{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function ProjectEdit() {
  const { id } = useParams();
  const pid = Number(id);
  const { items, label, meta, th } = useMeta();
  const isAdmin = meta.user.access_level >= th('manage_user');
  const { data, error } = useQuery({ queryKey: ['admin-project', pid], queryFn: () => api.get<any>(`/api/admin/projects/${pid}`) });
  const all = useQuery({ queryKey: ['admin-projects'], queryFn: () => api.get<any[]>('/api/admin/projects') });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<any[]>('/api/users') });
  const invalidate = useInvalidate('admin-project', 'admin-projects', 'meta', 'project');
  const navigate = useNavigate();
  const [p, setP] = useState<any | null>(null);
  const project = p ?? data?.project;

  const saveProject = useMutation({
    mutationFn: () =>
      api.patch(`/api/admin/projects/${pid}`, {
        name: project.name,
        description: project.description,
        status: Number(project.status),
        view_state: Number(project.view_state),
        enabled: !!project.enabled,
        inherit_global: !!project.inherit_global,
        ...(isAdmin ? { parent_ids: project.parent_ids } : {}),
      }),
    onSuccess: () => {
      setP(null);
      invalidate();
    },
  });

  const [cat, setCat] = useState('');
  const addCat = useMutation({ mutationFn: () => api.post(`/api/admin/projects/${pid}/categories`, { name: cat }), onSuccess: () => { setCat(''); invalidate(); } });
  const updCat = useMutation({ mutationFn: ({ id, body }: { id: number; body: any }) => api.patch(`/api/admin/categories/${id}`, body), onSuccess: invalidate });
  const delCat = useMutation({ mutationFn: (cid: number) => api.del(`/api/admin/categories/${cid}`), onSuccess: invalidate });

  const [ver, setVer] = useState({ version: '', date_order: '', released: false });
  const addVer = useMutation({ mutationFn: () => api.post(`/api/admin/projects/${pid}/versions`, ver), onSuccess: () => { setVer({ version: '', date_order: '', released: false }); invalidate(); } });
  const updVer = useMutation({ mutationFn: ({ id, body }: { id: number; body: any }) => api.patch(`/api/admin/versions/${id}`, body), onSuccess: invalidate });
  const delVer = useMutation({ mutationFn: (vid: number) => api.del(`/api/admin/versions/${vid}`), onSuccess: invalidate });

  const [member, setMember] = useState({ user_id: '', access_level: '55' });
  const addMember = useMutation({ mutationFn: () => api.put(`/api/admin/projects/${pid}/members`, { user_id: Number(member.user_id), access_level: Number(member.access_level) }), onSuccess: invalidate });
  const delMember = useMutation({ mutationFn: (uid: number) => api.del(`/api/admin/projects/${pid}/members/${uid}`), onSuccess: invalidate });

  const linkField = useMutation({
    mutationFn: async ({ fieldId, linked }: { fieldId: number; linked: boolean }) => {
      const fields = await api.get<any[]>('/api/admin/custom-fields');
      const f = fields.find((x) => x.id === fieldId);
      const ids: number[] = f.project_ids.filter((x: number) => x !== pid);
      if (linked) ids.push(pid);
      return api.patch(`/api/admin/custom-fields/${fieldId}`, { project_ids: ids });
    },
    onSuccess: invalidate,
  });

  if (error) return <ErrorBox error={error} />;
  if (!data || !project) return <div className="muted">{t('Loading…')}</div>;
  const set = (k: string) => (e: any) => setP({ ...project, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  return (
    <div>
      <p>
        <Link to="/manage/projects">{t('« Projects')}</Link>
      </p>
      <Panel title={t('Edit Project: {name}', { name: data.project.name })}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveProject.mutate();
          }}
        >
          <div className="form-grid three">
            <label>
              {t('Project Name')}
              <input value={project.name} onChange={set('name')} />
            </label>
            <label>
              {t('Status')}
              <select value={project.status} onChange={set('status')}>
                {items('project_status').map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('View Status')}
              <select value={project.view_state} onChange={set('view_state')}>
                <option value={10}>{t('public')}</option>
                <option value={50}>{t('private')}</option>
              </select>
            </label>
            <label className="check">
              <input type="checkbox" checked={!!project.enabled} onChange={set('enabled')} /> {t('Enabled')}
            </label>
            <label className="check">
              <input type="checkbox" checked={!!project.inherit_global} onChange={set('inherit_global')} /> {t('Inherit global categories')}
            </label>
            {isAdmin && (
              <label>
                {t('Subproject of')}
                <select
                  multiple
                  value={project.parent_ids.map(String)}
                  onChange={(e) => setP({ ...project, parent_ids: [...e.target.selectedOptions].map((o) => Number(o.value)) })}
                >
                  {(all.data ?? [])
                    .filter((x) => x.id !== pid)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                </select>
              </label>
            )}
          </div>
          <label className="block">
            {t('Description')}
            <textarea rows={3} value={project.description} onChange={set('description')} />
          </label>
          <ErrorBox error={saveProject.error} />
          <button className="primary" disabled={!p || saveProject.isPending}>
            {t('Update Project')}
          </button>
        </form>
      </Panel>

      <div className="two-col">
        <Panel title={t('Categories')}>
          <table className="simple">
            <tbody>
              {data.categories.map((c: any) => (
                <tr key={c.id}>
                  <td>
                    {c.project_id == null && <span className="muted">{t('[global]')} </span>}
                    {c.name}
                  </td>
                  <td>
                    {c.project_id != null && (
                      <select
                        value={c.default_handler_id ?? ''}
                        onChange={(e) => updCat.mutate({ id: c.id, body: { default_handler_id: e.target.value ? Number(e.target.value) : null } })}
                        title={t('Auto-assign new issues in this category to')}
                      >
                        <option value="">{t('(no default assignee)')}</option>
                        {users.data?.filter((u) => u.enabled).map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.username}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    {c.project_id != null && (
                      <>
                        <button
                          className="link"
                          onClick={() => {
                            const name = prompt(t('Rename category'), c.name);
                            if (name && name !== c.name) updCat.mutate({ id: c.id, body: { name } });
                          }}
                        >
                          {t('Rename')}
                        </button>{' '}
                        <button className="link danger" onClick={() => confirm(t('Delete category {name}?', { name: c.name })) && delCat.mutate(c.id)}>
                          {t('Delete')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (cat.trim()) addCat.mutate();
            }}
          >
            <input value={cat} onChange={(e) => setCat(e.target.value)} placeholder={t('New category')} />
            <button>{t('Add Category')}</button>
          </form>
          <ErrorBox error={addCat.error || updCat.error || delCat.error} />
        </Panel>

        <Panel title={t('Versions')}>
          <table className="simple">
            <thead>
              <tr>
                <th>{t('Version')}</th>
                <th>{t('Released')}</th>
                <th>{t('Obsolete')}</th>
                <th>{t('Date')}</th>
                <th className="num">{t('Issues')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.versions.map((v: any) => (
                <tr key={v.id}>
                  <td>{v.version}</td>
                  <td>
                    <input type="checkbox" checked={v.released} onChange={(e) => updVer.mutate({ id: v.id, body: { released: e.target.checked } })} />
                  </td>
                  <td>
                    <input type="checkbox" checked={v.obsolete} onChange={(e) => updVer.mutate({ id: v.id, body: { obsolete: e.target.checked } })} />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={fmtDate(v.date_order, false)}
                      onChange={(e) => e.target.value && updVer.mutate({ id: v.id, body: { date_order: e.target.value } })}
                    />
                  </td>
                  <td className="num">{v.issue_count}</td>
                  <td className="nowrap">
                    <button
                      className="link"
                      onClick={() => {
                        const name = prompt(t('Rename version (also updates issues)'), v.version);
                        if (name && name !== v.version) updVer.mutate({ id: v.id, body: { version: name } });
                      }}
                    >
                      {t('Rename')}
                    </button>{' '}
                    <button className="link danger" onClick={() => confirm(t('Delete version {name}?', { name: v.version })) && delVer.mutate(v.id)}>
                      {t('Delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (ver.version.trim()) addVer.mutate();
            }}
          >
            <input value={ver.version} onChange={(e) => setVer({ ...ver, version: e.target.value })} placeholder={t('New version')} className="short" />
            <input type="date" value={ver.date_order} onChange={(e) => setVer({ ...ver, date_order: e.target.value })} />
            <label className="check">
              <input type="checkbox" checked={ver.released} onChange={(e) => setVer({ ...ver, released: e.target.checked })} /> {t('released')}
            </label>
            <button>{t('Add Version')}</button>
          </form>
          <ErrorBox error={addVer.error || updVer.error || delVer.error} />
        </Panel>
      </div>

      <div className="two-col">
        <Panel title={t('Project Members (access overrides)')}>
          <table className="simple">
            <tbody>
              {data.members.map((m: any) => (
                <tr key={m.user_id}>
                  <td>
                    {m.username} <span className="muted">{m.realname}</span>
                  </td>
                  <td>{label('access_level', m.access_level)}</td>
                  <td>
                    <button className="link danger" onClick={() => delMember.mutate(m.user_id)}>
                      {t('Remove')}
                    </button>
                  </td>
                </tr>
              ))}
              {data.members.length === 0 && (
                <tr>
                  <td className="muted">{t("No per-project access overrides. Public projects use each member's team access level.")}</td>
                </tr>
              )}
            </tbody>
          </table>
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (member.user_id) addMember.mutate();
            }}
          >
            <select value={member.user_id} onChange={(e) => setMember({ ...member, user_id: e.target.value })}>
              <option value="">{t('user…')}</option>
              {users.data?.filter((u) => u.enabled).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username}
                </option>
              ))}
            </select>
            <select value={member.access_level} onChange={(e) => setMember({ ...member, access_level: e.target.value })}>
              {items('access_level').map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
            <button>{t('Add / Update')}</button>
          </form>
          <ErrorBox error={addMember.error || delMember.error} />
        </Panel>

        {isAdmin && (
          <Panel title={t('Custom Fields')}>
            {data.custom_fields.length === 0 && <p className="muted">{t('No custom fields defined.')}</p>}
            {data.custom_fields.map((f: any) => (
              <label key={f.id} className="check block">
                <input type="checkbox" checked={f.linked} onChange={(e) => linkField.mutate({ fieldId: f.id, linked: e.target.checked })} /> {f.name}
              </label>
            ))}
            <ErrorBox error={linkField.error} />
          </Panel>
        )}
      </div>

      {isAdmin && (
        <DangerZone
          title={t('Delete project')}
          button={t('Delete project')}
          confirmText={data.project.name}
          onConfirm={async () => {
            await api.post(`/api/admin/projects/${pid}/delete`, { name: data.project.name });
            invalidate();
            navigate('/manage/projects');
          }}
        >
          <p>
            {tp(
              all.data?.find((x) => x.id === pid)?.issue_count ?? 0,
              'Deletes the project with its {n} issue, their notes, history and attachments. This cannot be undone.',
              'Deletes the project with its {n} issues, their notes, history and attachments. This cannot be undone.',
            )}
          </p>
        </DangerZone>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
const CF_FLAGS: [string, string][] = [
  ['display_report', N_('Display on report')],
  ['display_update', N_('Display on update')],
  ['display_resolved', N_('Display on resolve')],
  ['display_closed', N_('Display on close')],
  ['require_report', N_('Required on report')],
  ['require_update', N_('Required on update')],
  ['require_resolved', N_('Required on resolve')],
  ['require_closed', N_('Required on close')],
  ['filter_by', N_('Add to filters')],
];

function CustomFieldsTab() {
  const { items, label, meta } = useMeta();
  const { data, error } = useQuery({ queryKey: ['admin-cf'], queryFn: () => api.get<any[]>('/api/admin/custom-fields') });
  const [editing, setEditing] = useState<any | null>(null);
  const invalidate = useInvalidate('admin-cf', 'admin-project', 'project');
  const save = useMutation({
    mutationFn: () => {
      const { id, ...rest } = editing;
      const body = {
        ...rest,
        type: Number(rest.type),
        access_level_r: Number(rest.access_level_r),
        access_level_rw: Number(rest.access_level_rw),
        length_min: Number(rest.length_min) || 0,
        length_max: Number(rest.length_max) || 0,
      };
      return id ? api.patch(`/api/admin/custom-fields/${id}`, body) : api.post('/api/admin/custom-fields', body);
    },
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });
  const del = useMutation({ mutationFn: (id: number) => api.del(`/api/admin/custom-fields/${id}`), onSuccess: invalidate });
  const set = (k: string) => (e: any) => setEditing({ ...editing, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  return (
    <Panel
      title={t('Custom Fields')}
      actions={
        <button
          className="primary"
          onClick={() =>
            setEditing({ name: '', type: 0, possible_values: '', default_value: '', valid_regexp: '', access_level_r: 10, access_level_rw: 25, length_min: 0, length_max: 0, display_report: true, display_update: true, filter_by: true, project_ids: [] })
          }
        >
          {t('New Custom Field')}
        </button>
      }
    >
      <ErrorBox error={error || del.error} />
      {editing && (
        <form
          className="subform"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="form-grid three">
            <label>
              {t('Name')}
              <input value={editing.name} onChange={set('name')} required />
            </label>
            <label>
              {t('Type')}
              <select value={editing.type} onChange={set('type')}>
                {items('custom_field_type').map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('Possible values (a|b|c)')}
              <input value={editing.possible_values} onChange={set('possible_values')} />
            </label>
            <label>
              {t('Default value')}
              <input value={editing.default_value} onChange={set('default_value')} />
            </label>
            <label>
              {t('Regular expression')}
              <input value={editing.valid_regexp} onChange={set('valid_regexp')} />
            </label>
            <label>
              {t('Read access')}
              <select value={editing.access_level_r} onChange={set('access_level_r')}>
                {items('access_level').map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('Write access')}
              <select value={editing.access_level_rw} onChange={set('access_level_rw')}>
                {items('access_level').map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('Min. length')}
              <input type="number" value={editing.length_min} onChange={set('length_min')} />
            </label>
            <label>
              {t('Max. length')}
              <input type="number" value={editing.length_max} onChange={set('length_max')} />
            </label>
          </div>
          <div className="checks">
            {CF_FLAGS.map(([k, l]) => (
              <label key={k} className="check">
                <input type="checkbox" checked={!!editing[k]} onChange={set(k)} /> {t(l)}
              </label>
            ))}
          </div>
          <label className="block">
            {t('Linked projects')}
            <select
              multiple
              value={(editing.project_ids ?? []).map(String)}
              onChange={(e) => setEditing({ ...editing, project_ids: [...e.target.selectedOptions].map((o) => Number(o.value)) })}
            >
              {meta.projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <ErrorBox error={save.error} />
          <div className="buttons">
            <button className="primary" disabled={save.isPending}>
              {t('Save')}
            </button>
            <button type="button" onClick={() => setEditing(null)}>
              {t('Cancel')}
            </button>
          </div>
        </form>
      )}
      <table className="simple">
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Type')}</th>
            <th>{t('Possible values')}</th>
            <th className="num">{t('Projects')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((f) => (
            <tr key={f.id}>
              <td>{f.name}</td>
              <td>{label('custom_field_type', f.type)}</td>
              <td className="muted">{f.possible_values}</td>
              <td className="num">{f.project_ids.length}</td>
              <td>
                <button className="link" onClick={() => setEditing(f)}>
                  {t('Edit')}
                </button>{' '}
                <button className="link danger" onClick={() => confirm(t('Delete custom field {name} and all its values?', { name: f.name })) && del.mutate(f.id)}>
                  {t('Delete')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}
